import {Navigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {Loading} from 'notiflix';
import {
  ADMIN_ONLY_MODULES,
  getModuleKeyForRouteKey,
  isActionAllowedForRouteKey,
  isRouteKeyAllowed,
} from 'utils/permissions';

// `routeKey` (optional) is the routes.js entry's `key` — when given, this
// enforces module-level RBAC (see utils/permissions.js) on top of plain
// authentication, so a restricted role can't bypass a hidden sidebar link by
// typing the URL directly.
//
// `action` (optional, requires `routeKey`) narrows that check from "can see
// this module at all" to "can perform this specific action" — use this on
// create/edit-only routes (e.g. a promo code create page needs
// action="create", not just "is promocodes visible"). Without `action`, any
// access to the module (even read-only) is enough.
const ProtectedRoute = ({children, routeKey, action}) => {
  const {user, isAuthenticated, loading, permissions, hasFullAccess, isSystemAdmin, permissionsLoaded} =
    useSelector(state => state.auth);

  const isAuthorized = Boolean(user);

  if (loading) {
    Loading.circle('Loading...');
    return null;
  }

  Loading.remove(); // Remove loading screen once auth is determined

  // If authentication check is done and the user is not authenticated, redirect immediately
  if (!isAuthenticated) {
    return <Navigate to="/authentication/sign-in" replace />;
  }

  // If user is authenticated but not authorized, also redirect
  if (!isAuthorized) {
    return <Navigate to="/authentication/sign-in" replace />;
  }

  // Wait for /my-permissions before deciding module access, so a restricted
  // role isn't briefly bounced to /dashboard on a hard refresh.
  const isAdminOnlyRoute = routeKey && ADMIN_ONLY_MODULES.has(getModuleKeyForRouteKey(routeKey));
  if (routeKey && (!hasFullAccess || isAdminOnlyRoute) && !permissionsLoaded) {
    Loading.circle('Loading...');
    return null;
  }

  if (routeKey && !isRouteKeyAllowed(routeKey, permissions, hasFullAccess, isSystemAdmin)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (
    routeKey &&
    action &&
    !isActionAllowedForRouteKey(routeKey, action, permissions, hasFullAccess, isSystemAdmin)
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default ProtectedRoute;
